import React from 'react';
import { CheckCircle, AlertTriangle, ShieldOff, Shield, Loader } from 'lucide-react';

export interface VerificationMetadata {
  mode?: string;
  enabled?: boolean;
  skipped?: boolean;
  skipped_reason?: string | null;
  trace_id?: string;
  module?: string | null;
  claims_extracted?: number;
  claims_verified?: number;
  claims_flagged?: number;
  claims_corrected?: number;
  claims_user_verified?: number;
  failure_rate?: number;
  needs_human_review?: boolean;
  confidence_score?: number;
  traces?: Array<{
    id?: string;
    claim: string;
    category?: string | null;
    source?: { quote?: string; hint?: string | null; quote_verified?: boolean } | null;
    question?: string;
    answer?: string;
    verdict?: string;
    confidence?: number;
    evidence?: string | null;
    status?: string;
    correction?: string;
    correction_reason?: string;
    user_corrected?: boolean;
    user_verified?: boolean;
    last_corrected_at?: string;
    history?: Array<{
      at: string;
      by_user_id?: number | null;
      previous_text: string;
      new_text: string;
      reason?: string;
      reverification_verdict?: string;
      reverification_confidence?: number;
      content_patched?: boolean;
      patch_location?: string | null;
    }>;
  }>;
  latency_ms?: number;
  generated_at?: string;
  error?: string;
}

interface VerificationBadgeProps {
  metadata?: VerificationMetadata | null;
  loading?: boolean;
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  className?: string;
}

const VerificationBadge: React.FC<VerificationBadgeProps> = ({
  metadata,
  loading = false,
  size = 'md',
  onClick,
  className = ''
}) => {
  const sizeClasses = size === 'sm'
    ? 'text-xs px-2 py-0.5 gap-1'
    : size === 'lg'
      ? 'text-sm px-3 py-1.5 gap-2'
      : 'text-xs px-2.5 py-1 gap-1.5';
  const iconSize = size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';

  if (loading) {
    return (
      <span className={`inline-flex items-center rounded-full bg-gray-100 text-gray-600 border border-gray-200 ${sizeClasses} ${className}`}>
        <Loader className={`${iconSize} animate-spin`} />
        Verifying…
      </span>
    );
  }

  if (!metadata || metadata.skipped || metadata.mode === 'off' || !metadata.enabled) {
    return (
      <span
        className={`inline-flex items-center rounded-full bg-gray-50 text-gray-500 border border-gray-200 ${sizeClasses} ${className} ${onClick ? 'cursor-pointer hover:bg-gray-100' : ''}`}
        onClick={onClick}
        title="Verification not enabled for this output"
      >
        <ShieldOff className={iconSize} />
        Unverified
      </span>
    );
  }

  if (metadata.error) {
    return (
      <span
        className={`inline-flex items-center rounded-full bg-orange-50 text-orange-700 border border-orange-200 ${sizeClasses} ${className} ${onClick ? 'cursor-pointer hover:bg-orange-100' : ''}`}
        onClick={onClick}
        title={metadata.error}
      >
        <AlertTriangle className={iconSize} />
        Verification error
      </span>
    );
  }

  const failureRate = metadata.failure_rate ?? 0;
  const claimsExtracted = metadata.claims_extracted ?? 0;
  const claimsVerified = metadata.claims_verified ?? 0;
  const claimsFlagged = metadata.claims_flagged ?? 0;
  const confidence = metadata.confidence_score ?? Math.round((1 - failureRate) * 100);
  const needsReview = metadata.needs_human_review || false;

  if (claimsExtracted === 0) {
    return (
      <span
        className={`inline-flex items-center rounded-full bg-gray-50 text-gray-500 border border-gray-200 ${sizeClasses} ${className} ${onClick ? 'cursor-pointer hover:bg-gray-100' : ''}`}
        onClick={onClick}
        title={metadata.skipped_reason || 'No claims extracted'}
      >
        <Shield className={iconSize} />
        No claims
      </span>
    );
  }

  if (needsReview || failureRate > 0.3) {
    return (
      <span
        className={`inline-flex items-center rounded-full bg-red-50 text-red-700 border border-red-200 ${sizeClasses} ${className} ${onClick ? 'cursor-pointer hover:bg-red-100' : ''}`}
        onClick={onClick}
        title={`${claimsFlagged} of ${claimsExtracted} claims failed verification — needs human review`}
      >
        <AlertTriangle className={iconSize} />
        Review needed · {confidence}%
      </span>
    );
  }

  if (failureRate > 0) {
    return (
      <span
        className={`inline-flex items-center rounded-full bg-amber-50 text-amber-700 border border-amber-200 ${sizeClasses} ${className} ${onClick ? 'cursor-pointer hover:bg-amber-100' : ''}`}
        onClick={onClick}
        title={`${claimsVerified}/${claimsExtracted} claims verified, ${claimsFlagged} flagged`}
      >
        <Shield className={iconSize} />
        Partially verified · {confidence}%
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center rounded-full bg-green-50 text-green-700 border border-green-200 ${sizeClasses} ${className} ${onClick ? 'cursor-pointer hover:bg-green-100' : ''}`}
      onClick={onClick}
      title={`All ${claimsExtracted} claims verified against source context`}
    >
      <CheckCircle className={iconSize} />
      Verified · {confidence}%
    </span>
  );
};

export default VerificationBadge;
